import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import moment from 'moment';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {KeyboardDateTimePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {SaveOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';

export const FORMATO_DATA_HORA_WIRE = 'YYYY-MM-DDTHH:mm:ss';
const FORMATO_DATA_HORA_TELA = 'DD/MM/YYYY HH:mm';

const dataValida = valor => !!valor && moment.isMoment(valor) && valor.isValid();

const FormSchema = Yup.object().shape({
    idTipo: Yup.string().required('É obrigatório'),
    dataInicio: Yup.mixed()
        .test('obrigatoria', 'É obrigatória', valor => !!valor)
        .test('valida', 'Data inválida', valor => !valor || dataValida(valor)),
    dataFim: Yup.mixed()
        .test('obrigatoria', 'É obrigatória', valor => !!valor)
        .test('valida', 'Data inválida', valor => !valor || dataValida(valor))
        .test('posterior', 'Deve ser posterior ao início', function posterior(valor) {
            const inicio = this.parent.dataInicio;
            return !dataValida(valor) || !dataValida(inicio) || valor.isAfter(inicio);
        })
});

const rawObject = {idTipo: '', dataInicio: null, dataFim: null};

const paraFormulario = value => (value ? {
    idTipo: value.idTipo === null || value.idTipo === undefined ? '' : String(value.idTipo),
    dataInicio: value.dataInicio ? moment(value.dataInicio, FORMATO_DATA_HORA_WIRE) : null,
    dataFim: value.dataFim ? moment(value.dataFim, FORMATO_DATA_HORA_WIRE) : null
} : rawObject);

const useStyles = makeStyles(() => ({
    root: {
        width: 800
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Calendário da inscrição (_formCriarCalendario do PrimeFaces): tipo
 * (entre os tipos de calendário do processo) e data/hora de início e término.
 */
export default function DialogCalendarioInscricao({open, onClose, title, value, tiposCalendario = [], onSubmit}) {
    const classes = useStyles();
    const {control, handleSubmit, reset, formState: {errors, isSubmitting}} =
        useForm({resolver: yupResolver(FormSchema), defaultValues: paraFormulario(value)});

    useEffect(() => {
        if (open) {
            reset(paraFormulario(value));
        }
    }, [open, value]);

    /* Re-popula a cada abertura (padrão §1.7). */
    const onEntering = () => reset(paraFormulario(value));

    const handleClose = () => {
        onClose();
        reset(rawObject);
    };

    const submit = valores => {
        const tipo = tiposCalendario.find(item => String(item.id) === String(valores.idTipo));
        onSubmit({
            ...(value || {}),
            id: value?.id ?? null,
            idTipo: Number(valores.idTipo),
            descricaoTipo: tipo?.descricao || value?.descricaoTipo || '',
            dataInicio: valores.dataInicio.format(FORMATO_DATA_HORA_WIRE),
            dataFim: valores.dataFim.format(FORMATO_DATA_HORA_WIRE)
        });
        handleClose();
    };

    /* O diálogo fica dentro do form da página na árvore do React: o submit não pode subir até ele. */
    const aoEnviar = evento => {
        evento.stopPropagation();
        handleSubmit(submit)(evento);
    };

    const campoDataHora = (nome, rotulo) => (
        <Grid item xs={12} sm={6}>
            <LabelValue label={rotulo}>
                <Controller
                    name={nome}
                    control={control}
                    render={({field}) => (
                        <KeyboardDateTimePicker
                            value={field.value}
                            onChange={data => field.onChange(data)}
                            onBlur={field.onBlur}
                            format={FORMATO_DATA_HORA_TELA}
                            placeholder='dd/mm/aaaa hh:mm'
                            mask='__/__/____ __:__'
                            ampm={false}
                            cancelLabel='Cancelar'
                            okLabel='OK'
                            invalidDateMessage='Data inválida'
                            inputVariant='outlined'
                            fullWidth
                            error={!!errors[nome]}
                            helperText={errors[nome]?.message}/>
                    )}/>
            </LabelValue>
        </Grid>
    );

    return (
        <Dialog open={open} onClose={handleClose} TransitionProps={{onEntering}} scroll='body'
                aria-labelledby='dialog-calendario-inscricao-titulo' classes={{paper: classes.root}}>
            <DialogTitle id='dialog-calendario-inscricao-titulo'>{title}</DialogTitle>
            <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                <form noValidate autoComplete='off' onSubmit={aoEnviar}>
                    <DialogContent classes={{root: classes.contentRoot}}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <LabelValue label='Tipo do calendário *'>
                                    <FormControl variant='outlined' fullWidth error={!!errors.idTipo}>
                                        <Controller
                                            name='idTipo'
                                            control={control}
                                            render={({field}) => (
                                                <Select {...field} displayEmpty>
                                                    <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                                    {tiposCalendario.map(tipo => (
                                                        <MenuItem key={tipo.id} value={String(tipo.id)}>{tipo.descricao}</MenuItem>
                                                    ))}
                                                </Select>
                                            )}/>
                                        {!!errors.idTipo && <FormHelperText error>{errors.idTipo.message}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            {campoDataHora('dataInicio', 'Data e hora de início *')}
                            {campoDataHora('dataFim', 'Data e hora de término *')}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <TextButton onClick={handleClose}>Fechar</TextButton>
                        <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>Salvar</ContainedButton>
                    </DialogActions>
                </form>
            </MuiPickersUtilsProvider>
        </Dialog>
    );
}

DialogCalendarioInscricao.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.object,
    tiposCalendario: PropTypes.array,
    onSubmit: PropTypes.func.isRequired
};
